import { ClockIcon } from '@synoptic/ui-kit/icons/react/clock.js';
import { XmarkIcon } from '@synoptic/ui-kit/icons/react/xmark.js';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@synoptic/ui-kit/popover/popover.js';
import {
  MouseEvent,
  useCallback,
  useEffect,
  useState,
  useTransition,
} from 'react';
import { useSearchParams } from 'react-router';
import { deleteSearchParams } from '@/utils/handle-search-param';
import { useStreamPostsLoaderData } from './loader';
import {
  clearButton,
  clearIcon,
  dateInput,
  field,
  fieldLabel,
  footer,
  footerButton,
  popover,
  trigger,
  triggerIcon,
} from './stream-posts-date-range.css';

const formatCompact = (local: string): string => {
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return local;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
};

const buildLabel = (from: string, to: string): string => {
  if (from && to) return `${formatCompact(from)} – ${formatCompact(to)}`;
  if (from) return `From ${formatCompact(from)}`;
  if (to) return `Until ${formatCompact(to)}`;
  return 'Date range';
};

export const StreamPostsDateRange = () => {
  const { from, to } = useStreamPostsLoaderData();
  const [, setSearchParams] = useSearchParams();
  const [, startTransition] = useTransition();
  const [open, setOpen] = useState(false);

  const [fromLocal, setFromLocal] = useState(from);
  const [toLocal, setToLocal] = useState(to);

  useEffect(() => {
    setFromLocal(from);
  }, [from]);
  useEffect(() => {
    setToLocal(to);
  }, [to]);

  const applyParams = useCallback(
    (nextFrom: string, nextTo: string) => {
      startTransition(() => {
        setSearchParams(
          (prev) => {
            const next = deleteSearchParams(prev, ['from', 'to']);
            if (nextFrom) next.set('from', nextFrom);
            if (nextTo) next.set('to', nextTo);
            return next;
          },
          { preventScrollReset: true },
        );
      });
    },
    [setSearchParams],
  );

  const handleFromChange = (value: string) => {
    setFromLocal(value);
    applyParams(value, toLocal);
  };

  const handleToChange = (value: string) => {
    setToLocal(value);
    applyParams(fromLocal, value);
  };

  const handleClear = (e?: MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    setFromLocal('');
    setToLocal('');
    applyParams('', '');
  };

  const isActive = Boolean(from || to);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={trigger}
        data-active={isActive ? 'true' : undefined}
        aria-label="Filter posts by date range"
      >
        <ClockIcon className={triggerIcon} />
        <span>{buildLabel(from, to)}</span>
        {isActive && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear date range"
            className={clearButton}
            onClick={handleClear}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleClear();
            }}
          >
            <XmarkIcon className={clearIcon} />
          </span>
        )}
      </PopoverTrigger>
      <PopoverContent
        className={popover}
        side="bottom"
        align="end"
        sideOffset={6}
      >
        <label className={field}>
          <span className={fieldLabel}>From</span>
          <input
            type="datetime-local"
            className={dateInput}
            value={fromLocal}
            max={toLocal || undefined}
            onChange={(e) => handleFromChange(e.target.value)}
          />
        </label>

        <label className={field}>
          <span className={fieldLabel}>To</span>
          <input
            type="datetime-local"
            className={dateInput}
            value={toLocal}
            min={fromLocal || undefined}
            onChange={(e) => handleToChange(e.target.value)}
          />
        </label>

        {isActive && (
          <div className={footer}>
            <button
              type="button"
              className={footerButton}
              onClick={() => handleClear()}
            >
              Clear
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
